import * as React from 'react';
import * as RadixSelect from '@radix-ui/react-select';
import { Check, ChevronDown, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLang } from '../../i18n';
import { cn } from '../../lib/utils';

/**
 * Select — the single-choice control of the system (DS-30…33).
 *
 * Radix drives it because a portalled listbox inside a dense, scrolling ERP
 * screen needs real focus management and collision detection. Where a portal
 * would fight the layout — editing a cell inside a table row — use
 * `NativeSelect` instead.
 */

export interface SelectOption {
  value: string;
  label: string;
  /** Secondary line shown at the end of the row; never rendered in the trigger. */
  description?: string;
  /** DS-53 — provider / procedure-category colour, drawn as a leading dot. */
  color?: string;
  disabled?: boolean;
  icon?: LucideIcon;
}

type SelectSize = 'sm' | 'md';

/** DS-30 — same padding as `Input`'s shell, so a select and a text field of the
 *  same size stand the same height side by side in a filter row. */
const SIZE_TRIGGER: Record<SelectSize, string> = {
  sm: 'p-1 text-[11px]',
  md: 'p-1.5 text-xs',
};

const SIZE_ITEM: Record<SelectSize, string> = {
  sm: 'py-0.5 text-[11px]',
  md: 'py-1 text-xs',
};

export interface SelectProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof RadixSelect.Trigger>,
    'value' | 'defaultValue' | 'onChange' | 'dir'
  > {
  /** Empty string or null shows the placeholder. */
  value?: string | null;
  /** Fires with the chosen option's value; the clear button fires with `''`. */
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  /** Truthy renders the invalid state; the message itself belongs to `FieldError`. */
  error?: boolean | string;
  disabled?: boolean;
  clearable?: boolean;
  size?: SelectSize;
  /** Shown inside the list when `options` is empty. */
  emptyMessage?: string;
  /** Submitted by the hidden native select Radix renders for plain forms. */
  name?: string;
  required?: boolean;
  /** DS-50 — defaults to the direction the interface is currently running in. */
  dir?: 'rtl' | 'ltr';
  clearLabel?: string;
}

export const Select = React.forwardRef<React.ElementRef<typeof RadixSelect.Trigger>, SelectProps>(
  function Select(
    {
      value,
      onChange,
      options,
      placeholder: placeholderProp,
      error,
      disabled,
      clearable,
      size = 'md',
      emptyMessage: emptyMessageProp,
      name,
      required,
      dir: dirProp,
      clearLabel: clearLabelProp,
      className,
      ...rest
    },
    ref,
  ) {
    const { t } = useTranslation('common');
    const lang = useLang();
    const placeholder = placeholderProp ?? t('ui.select.placeholder');
    const emptyMessage = emptyMessageProp ?? t('ui.select.empty');
    const clearLabel = clearLabelProp ?? t('ui.select.clear');
    // DS-50 — a hard 'rtl' default would portal an English listbox right-aligned
    // over a left-aligned form.
    const dir = dirProp ?? (lang === 'EN' ? 'ltr' : 'rtl');

    // Radix treats '' as "nothing picked", which is exactly the placeholder state.
    const current = value ?? '';
    const showClear = Boolean(clearable && current && !disabled);

    return (
      <RadixSelect.Root
        dir={dir}
        value={current}
        onValueChange={onChange}
        disabled={disabled}
        name={name}
        required={required}
      >
        <div className="relative">
          <RadixSelect.Trigger
            ref={ref}
            aria-invalid={error ? true : undefined}
            className={cn(
              'input-base flex items-center gap-1.5 text-start',
              SIZE_TRIGGER[size],
              'data-[placeholder]:text-slate-400',
              showClear && 'pe-9',
              error && 'border-red-500 focus:ring-red-500',
              className,
            )}
            {...rest}
          >
            <span className="flex-1 truncate">
              <RadixSelect.Value placeholder={placeholder} />
            </span>
            <RadixSelect.Icon asChild>
              <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            </RadixSelect.Icon>
          </RadixSelect.Trigger>

          {showClear && (
            <button
              type="button"
              aria-label={clearLabel}
              data-testid="select-clear"
              onClick={() => onChange('')}
              className="absolute end-5 top-1/2 z-10 flex h-4 w-4 -translate-y-1/2 items-center justify-center rounded text-slate-400 transition hover:bg-red-50 hover:text-red-600"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        <RadixSelect.Portal>
          <RadixSelect.Content
            data-testid="select-listbox"
            dir={dir}
            position="popper"
            sideOffset={4}
            className={cn(
              'z-50 max-h-72 min-w-[var(--radix-select-trigger-width)] overflow-hidden',
              'rounded border border-slate-300 bg-white shadow-lg',
              'data-[state=open]:animate-zoomIn',
            )}
          >
            {/* No scroll buttons: the wheel and the arrow keys already move the
                list. Radix hides the viewport's scrollbar for everyone the moment
                a Viewport mounts, on the assumption those buttons are the
                affordance — so the overflow hint has to be handed back by force,
                or a long list would look like it ends at the last visible row.
                `!` beats Radix's rule, which is injected unlayered. */}
            <RadixSelect.Viewport className="p-1 ![scrollbar-width:thin] [&::-webkit-scrollbar]:!block">
              {options.length === 0 && (
                <div className="px-2 py-3 text-center text-[11px] text-slate-400">{emptyMessage}</div>
              )}
              {options.map((option) => {
                const Icon = option.icon;
                return (
                  <RadixSelect.Item
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    data-testid="select-option"
                    data-value={option.value}
                    className={cn(
                      'relative flex cursor-pointer select-none items-center gap-2 rounded ps-6 pe-2 outline-none',
                      SIZE_ITEM[size],
                      'data-[highlighted]:bg-blue-50 data-[highlighted]:text-blue-700',
                      'data-[state=checked]:font-bold',
                      'data-[disabled]:pointer-events-none data-[disabled]:text-slate-400',
                    )}
                  >
                    <RadixSelect.ItemIndicator className="absolute start-1.5 inline-flex items-center">
                      <Check className="h-3 w-3 text-blue-600" />
                    </RadixSelect.ItemIndicator>

                    {/* Radix mirrors ItemText into the trigger, so the dot and icon
                        travel with the label once the row is selected. */}
                    <RadixSelect.ItemText>
                      <span className="flex items-center gap-1.5">
                        {option.color && (
                          <span
                            className="h-2 w-2 shrink-0 rounded-full"
                            style={{ backgroundColor: option.color }}
                          />
                        )}
                        {Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-slate-500" />}
                        <span className="truncate">{option.label}</span>
                      </span>
                    </RadixSelect.ItemText>

                    {option.description && (
                      <span className="ms-auto shrink-0 ps-3 text-[10px] font-normal text-slate-400">
                        {option.description}
                      </span>
                    )}
                  </RadixSelect.Item>
                );
              })}
            </RadixSelect.Viewport>
          </RadixSelect.Content>
        </RadixSelect.Portal>
      </RadixSelect.Root>
    );
  },
);

// ---------------------------------------------------------------------------
// NativeSelect
// ---------------------------------------------------------------------------

export interface NativeSelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  options: SelectOption[];
  /** Rendered as the empty-value row so the field can be cleared in place. */
  placeholder?: string;
  error?: boolean | string;
}

/**
 * The browser's own select, wearing the design system. Used for in-cell editing
 * inside a table (DS-22): no portal to escape the scroll container, no layer to
 * close before the next row can be clicked, and it opens on the keyboard alone.
 * Keeps the native `onChange(event)` signature so `register()` binds directly.
 */
export const NativeSelect = React.forwardRef<HTMLSelectElement, NativeSelectProps>(
  function NativeSelect({ options, placeholder, error, className, ...rest }, ref) {
    return (
      <select
        ref={ref}
        aria-invalid={error ? true : undefined}
        className={cn(
          'input-base cursor-pointer pe-6',
          error && 'border-red-500 focus:ring-red-500',
          className,
        )}
        {...rest}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
    );
  },
);
